import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { isPaidPlan } from "@/lib/plans";

type OrgRole = "owner" | "admin" | "member" | "viewer";

export interface Organization {
  id: string;
  name: string;
  slug: string | null;
  website: string | null;
  industry: string | null;
  logo_url: string | null;
  subscription_plan: "free" | "pro" | "enterprise";
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMembership {
  organization: Organization;
  role: OrgRole;
  joined_at: string;
}

interface OrganizationContextType {
  organizations: OrganizationMembership[];
  currentOrganization: Organization | null;
  currentRole: OrgRole | null;
  isLoading: boolean;
  isPremium: boolean;
  setCurrentOrganization: (org: Organization | null) => void;
  refreshOrganizations: () => Promise<void>;
  canManageOrg: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};

const ORG_STORAGE_KEY = "glever_current_org_id";

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAdmin } = useAuth();
  const [organizations, setOrganizations] = useState<OrganizationMembership[]>([]);
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);
  const [currentRole, setCurrentRole] = useState<OrgRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrganizations = async () => {
    if (!user?.id) {
      setOrganizations([]);
      setCurrentOrganizationState(null);
      setCurrentRole(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      let orgs: OrganizationMembership[] = [];

      if (isAdmin) {
        // Admins can see ALL organizations with "admin" role for management
        const { data: allOrgs, error: orgsError } = await supabase
          .from("organizations")
          .select("*")
          .order("name", { ascending: true });

        if (orgsError) {
          console.error("Error fetching all organizations:", orgsError);
          setIsLoading(false);
          return;
        }

        orgs = (allOrgs || []).map((org: any) => ({
          organization: org as Organization,
          role: "admin" as OrgRole, // Admins have admin access to all orgs
          joined_at: org.created_at,
        }));
      } else {
        // Regular users only see orgs they're members of
        const { data: memberships, error } = await supabase
          .from("organization_members")
          .select(`
            role,
            joined_at,
            organizations (
              id,
              name,
              slug,
              website,
              industry,
              logo_url,
              subscription_plan,
              is_active,
              created_at,
              updated_at
            )
          `)
          .eq("user_id", user.id)
          .eq("status", "active");

        if (error) {
          console.error("Error fetching organizations:", error);
          setIsLoading(false);
          return;
        }

        orgs = (memberships || [])
          .filter((m: any) => m.organizations)
          .map((m: any) => ({
            organization: m.organizations as Organization,
            role: m.role as OrgRole,
            joined_at: m.joined_at,
          }));
      }

      setOrganizations(orgs);

      // Restore previous selection or pick first org
      const storedOrgId = localStorage.getItem(ORG_STORAGE_KEY);
      let selectedOrg = orgs.find(o => o.organization.id === storedOrgId);
      
      if (!selectedOrg && orgs.length > 0) {
        selectedOrg = orgs[0];
      }

      if (selectedOrg) {
        setCurrentOrganizationState(selectedOrg.organization);
        setCurrentRole(selectedOrg.role);
        localStorage.setItem(ORG_STORAGE_KEY, selectedOrg.organization.id);
      } else {
        setCurrentOrganizationState(null);
        setCurrentRole(null);
        localStorage.removeItem(ORG_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Error in fetchOrganizations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const setCurrentOrganization = (org: Organization | null) => {
    setCurrentOrganizationState(org);
    if (org) {
      localStorage.setItem(ORG_STORAGE_KEY, org.id);
      const membership = organizations.find(o => o.organization.id === org.id);
      setCurrentRole(membership?.role || null);
    } else {
      localStorage.removeItem(ORG_STORAGE_KEY);
      setCurrentRole(null);
    }
  };

  const refreshOrganizations = async () => {
    await fetchOrganizations();
  };

  useEffect(() => {
    fetchOrganizations();
  }, [user?.id, isAdmin]);

  // Can manage org if owner, admin, or platform admin
  const canManageOrg = currentRole === "owner" || currentRole === "admin" || isAdmin;

  // Premium is org-level, not user-level. Admins always get premium access.
  const isPremium = isAdmin || isPaidPlan(currentOrganization?.subscription_plan);

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrganization,
        currentRole,
        isLoading,
        isPremium,
        setCurrentOrganization,
        refreshOrganizations,
        canManageOrg,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
